/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowData,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import {useState, FC, useEffect, useMemo} from 'react'
import {Row, Col, Pagination, Form, Spinner, Accordion, Button} from 'react-bootstrap'
import Select from 'react-select'
import {ListAgreementResultRow} from '../core/_models'
import {Link, useNavigate} from 'react-router-dom'
import {getISO2ByISO3, getNameByISO3} from '../../../../libs/misc'
import {
  AGREEMENT_STATUS_OPTIONS,
  AgreementStatusType,
  getAgreementStatusLabel,
  isWtoAgreementType,
} from '../../../../libs/constant'

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClass: string
    cellClass: string
  }
}

type ListAgreementTableProps = {
  rows?: ListAgreementResultRow[] | null
  isLoading: boolean
}

const ListAgreementTable: FC<ListAgreementTableProps> = ({rows, isLoading}) => {
  const navigate = useNavigate()
  const [internalLoading, setInternalLoading] = useState(false)
  const [listAgreement, setListAgreement] = useState<ListAgreementResultRow[]>([])
  const [selectedSort, setSelectedSort] = useState<string | null>(null)
  const [displaySortText, setDisplaySortText] = useState('Sort by Country')

  const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 10})

  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<AgreementStatusType>(null)
  const [selectedParties, setSelectedParties] = useState<string[]>([])
  const [strictPartyMatch, setStrictPartyMatch] = useState(false)
  const [includeWtoAgreements, setIncludeWtoAgreements] = useState(false)

  const getSortParam = (sort: string | null) => {
    switch (sort) {
      case 'country_asc':
        return {
          displayText: 'Country Ascending',
          sorting: [{id: 'country', desc: false}],
        }
      case 'country_desc':
        return {
          displayText: 'Country Descending',
          sorting: [{id: 'country', desc: true}],
        }
      default:
        return {
          displayText: 'Sort by Country',
          sorting: [],
        }
    }
  }

  const handleSelectSort = (sort: string | null) => {
    if (selectedSort !== sort) {
      setPagination({
        pageIndex: 1,
        pageSize: 10,
      })
    }
    const value = sort === 'reset' ? null : sort
    setSelectedSort(value)

    const {displayText, sorting} = getSortParam(value)
    setDisplaySortText(displayText)
    setSorting(sorting) // this comes from props, passed from the main page
  }

  const handleResetFilters = () => {
    setPagination({pageIndex: 0, pageSize: pagination.pageSize})
    setSelectedStatus(null)
    setSelectedParties([])
    setStrictPartyMatch(false)
    setIncludeWtoAgreements(false)
  }

  // Shared control height + styles so the Status and Parties react-select
  // fields render with identical control/menu styling.
  const FILTER_CONTROL_HEIGHT = '38px'
  const filterSelectStyles = {
    control: (base: any) => ({...base, minHeight: FILTER_CONTROL_HEIGHT}),
    menuPortal: (base: any) => ({...base, zIndex: 9999}),
  }

  const columnHelper = createColumnHelper<ListAgreementResultRow>()

  const columns = [
    columnHelper.accessor('id', {
      id: 'agreement_id',
      header: 'ID',
      cell: (info) => info.getValue(),
      meta: {
        headerClass: 'text-start w-75px',
        cellClass: 'text-start w-75px',
      },
    }),
    columnHelper.accessor('acronym', {
      header: 'Short Name',
      cell: (info) => {
        return info.getValue()
      },
      meta: {
        headerClass: 'ps-4 text-start w-100px',
        cellClass: 'text-start w-100px',
      },
    }),
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => {
        return info.getValue()
      },
      meta: {
        headerClass: 'text-start w-200px',
        cellClass: 'text-start w-200px',
      },
      sortUndefined: 'last',
    }),
    columnHelper.accessor('iso', {
      header: 'Parties',
      cell: (info) => {
        if (!info.getValue()) return <span>-</span>
        const countries = info.getValue().split(';')
        return (
          <div className='countries'>
            {countries.map((rawCountry: string, idx: number) => {
              const country = rawCountry.trim()
              let iso2 = getISO2ByISO3(country)
              if (country === 'EUN') iso2 = 'EU'

              // 🔑 Explicit EUN handling OR fallback
              return country === 'TWN' || !iso2 ? (
                <span key={idx} className='badge text-white bg-dark me-1' title={country}>
                  {country}
                </span>
              ) : (
                <span key={idx} className={`fi fi-${iso2.toLowerCase()} me-2`} title={country} />
              )
            })}
          </div>
        )
      },
      meta: {
        headerClass: 'text-start w-250px',
        cellClass: 'text-start w-250px',
      },
      sortUndefined: 'last',
    }),
    columnHelper.accessor('date_signed', {
      header: 'Date Signed',
      cell: (info) => {
        return info.getValue()
      },
      meta: {
        headerClass: 'text-start w-100px',
        cellClass: 'text-start w-100px',
      },
      sortUndefined: 'last',
    }),
    columnHelper.accessor('date_into_force', {
      header: 'Date Into Force',
      cell: (info) => {
        return info.getValue() ?? '-'
      },
      meta: {
        headerClass: 'text-start w-100px',
        cellClass: 'text-start w-100px',
      },
      sortUndefined: 'last',
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => {
        return info.getValue()
      },
      meta: {
        headerClass: 'text-center w-75px',
        cellClass: 'text-center w-75px',
      },
      sortUndefined: 'last',
    }),
    columnHelper.accessor('article_count', {
      header: 'Articles',
      cell: (info) => info.getValue() ?? 0,
      meta: {
        headerClass: 'text-center w-75px',
        cellClass: 'text-center w-75px',
      },
      sortUndefined: 'last',
    }),
    columnHelper.accessor('id', {
      header: '',
      cell: (info) => {
        return (
          <Link to={`/agreement/${info.getValue()}`}>
            <span className='fa fa-eye'></span>
          </Link>
        )
      },
      meta: {
        headerClass: '',
        cellClass: 'text-center w-50px',
      },
      enableSorting: false,
    }),
  ]

  // Cleanup function to reset the state when the component is unmounted
  useEffect(() => {
    setListAgreement([])
    setInternalLoading(true)
    return () => {
      setListAgreement([])
      setInternalLoading(false)
    }
  }, [])

  useEffect(() => {
    setInternalLoading(isLoading)
  }, [isLoading])

  useEffect(() => {
    setListAgreement(rows!)
    setInternalLoading(false)
  }, [rows])

  // Options are built from the ISO3 codes actually present in the loaded
  // rows (not the full world list) so the dropdown only offers parties that
  // appear in at least one agreement.
  const partyOptions = useMemo(() => {
    if (!rows) return []
    const codes = new Set<string>()
    rows.forEach((row) => {
      row.iso
        ?.split(';')
        .map((c) => c.trim())
        .filter(Boolean)
        .forEach((c) => codes.add(c))
    })
    return Array.from(codes)
      .sort()
      .map((code) => ({value: code, label: `${getNameByISO3(code)} (${code})`}))
  }, [rows])

  const filteredData = useMemo(() => {
    if (!rows) return []

    let result = rows

    if (!includeWtoAgreements) {
      result = result.filter((row) => !isWtoAgreementType(row.type))
    }

    if (selectedStatus) {
      const label = getAgreementStatusLabel(selectedStatus)
      result = result.filter((row) => row.status === label)
    }

    if (selectedParties.length > 0) {
      result = result.filter((row) => {
        const members = row.iso ? row.iso.split(';').map((c) => c.trim()) : []
        return strictPartyMatch
          ? selectedParties.every((party) => members.includes(party))
          : selectedParties.some((party) => members.includes(party))
      })
    }

    return result
  }, [rows, selectedStatus, selectedParties, strictPartyMatch, includeWtoAgreements])

  const dataTable = useReactTable({
    data: filteredData || [],
    columns,
    state: {
      pagination,
      sorting,
      globalFilter,
    },
    // No explicit pageCount here — it would freeze page numbers at the
    // status/parties-filtered count and ignore the search box's own
    // globalFilter narrowing, leaving stale page numbers that slice into
    // rows that no longer exist once search shrinks the result set. Client
    // pagination (getPaginationRowModel below) derives page count from the
    // fully filtered row model automatically when pageCount is omitted.
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  function paginationRange() {
    const currentPage = pagination.pageIndex + 1
    const totalPages = dataTable.getPageCount()
    const maxPagesToShow = 3
    const halfRange = Math.floor(maxPagesToShow / 2)

    // Ensure startPage and endPage are within valid range
    let startPage = Math.max(1, currentPage - halfRange)
    let endPage = Math.min(totalPages, currentPage + halfRange)

    // Adjust range to maintain maxPagesToShow
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1)
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)
      }
    }

    const range: any = []

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
      range.push(1)
      if (startPage > 2) {
        range.push('...')
      }
    }

    // Add pages in the calculated range
    for (let i = startPage; i <= endPage; i++) {
      range.push(i)
    }

    // Add last page and ellipsis if necessary
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...')
      }
      range.push(totalPages)
    }

    return range
  }

  const handleNavigationPageChange = (nav) => {
    setPagination({
      ...pagination,
      pageIndex: nav === 'prev' ? Math.max(0, pagination.pageIndex - 1) : pagination.pageIndex + 1,
    })
  }

  const handleDisplayPageChange = (e) => {
    setPagination({
      ...pagination,
      pageIndex: 1,
      pageSize: +e.target.value,
    })
  }

  const pages = paginationRange()
  // Post search+status+parties count (react-table's own filtered row model),
  // not just filteredData.length, since that only reflects status/parties —
  // the search box's globalFilter narrows further on top of it.
  const totalFilteredCount = dataTable.getFilteredRowModel().rows.length
  const firstIndexPerPages =
    dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize + 1

  const lastIndexPerPages =
    dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize +
    dataTable.getRowModel().rows.length

  const displayHeader = dataTable.getHeaderGroups().map((headerGroup) => (
    <tr className='fw-normal py-4 align-middle' key={headerGroup.id}>
      {headerGroup.headers.map((header) => {
        return (
          <th key={header.id} className={header.column.columnDef.meta?.headerClass}>
            <div
              className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
              title={
                header.column.getCanSort()
                  ? header.column.getNextSortingOrder() === 'asc'
                    ? 'Sort ascending'
                    : header.column.getNextSortingOrder() === 'desc'
                    ? 'Sort descending'
                    : 'Reset'
                  : undefined
              }
              onClick={header.column.getToggleSortingHandler()}
            >
              {flexRender(header.column.columnDef.header, header.getContext())}
              {header.column.getCanSort() && (
                <span className='ms-2'>
                  {{
                    asc: <i className='fa-solid fa-sort-up'></i>,
                    desc: <i className='fa-solid fa-sort-down'></i>,
                    false: <i className='fa-solid fa-sort'></i>,
                  }[header.column.getIsSorted() as string] ?? <i className='fa-solid fa-sort'></i>}
                </span>
              )}
            </div>
          </th>
        )
      })}
    </tr>
  ))
  const displayBody =
    dataTable.getRowModel().rows.length > 0 ? (
      dataTable.getRowModel().rows.map((row) => (
        <tr key={row.id} className='align-middle'>
          {row.getVisibleCells().map((cell) => {
            return (
              <td key={cell.id} className={cell.column.columnDef.meta?.cellClass}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            )
          })}
        </tr>
      ))
    ) : (
      <tr className='align-middle'>
        <td colSpan={99} className='text-center'>
          No Data
        </td>
      </tr>
    )

  return (
    <div style={{minHeight: '400px', position: 'relative'}}>
      {!internalLoading ? (
        <>
          <Row className='mb-5 justify-content-between'>
            <Col lg={5} md={5} className='d-flex justify-content-start align-items-center'>
              <i className='fa-solid fa-circle text-success fs-9 me-2'></i>Showing{' '}
              {totalFilteredCount > 0 ? firstIndexPerPages : 0} to{' '}
              {totalFilteredCount > 0 ? lastIndexPerPages : 0} of {totalFilteredCount} results
            </Col>
            <Col lg={7} md={7} className='action-table'>
              <Form.Group className='form-group' controlId=''>
                <div className='input-with-icon'>
                  <Form.Control
                    type='text'
                    className='form-control-md'
                    onChange={(e) => {
                      setPagination({
                        pageIndex: 0,
                        pageSize: 10,
                      })
                      setGlobalFilter(e.target.value)
                    }}
                    placeholder='Search by Name'
                    value={globalFilter}
                  />
                  <div className='input-icon'>
                    <i className='fa-solid fa-magnifying-glass fa-rotate-90 text-primary'></i>
                  </div>
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Accordion defaultActiveKey={null} className='mb-5'>
            <Accordion.Item eventKey='0'>
              <Accordion.Header>
                Show Filters <i className='la la-filter ms-2'></i>
              </Accordion.Header>
              <Accordion.Body>
                <Row className='g-4'>
                  <Col md={3}>
                    <Form.Label>Status</Form.Label>
                    <Select
                      isClearable
                      placeholder='Filter by status'
                      options={AGREEMENT_STATUS_OPTIONS}
                      value={
                        AGREEMENT_STATUS_OPTIONS.find(
                          (option) => option.value === selectedStatus
                        ) || null
                      }
                      onChange={(selected) => {
                        setPagination({pageIndex: 0, pageSize: pagination.pageSize})
                        setSelectedStatus(selected ? (selected.value as AgreementStatusType) : null)
                      }}
                      menuPortalTarget={document.body}
                      styles={filterSelectStyles}
                    />
                  </Col>
                  <Col md={5}>
                    <Form.Label>Parties</Form.Label>
                    <Select
                      isMulti
                      isClearable
                      placeholder='Filter by parties'
                      options={partyOptions}
                      value={partyOptions.filter((option) =>
                        selectedParties.includes(option.value)
                      )}
                      onChange={(selected) => {
                        setPagination({pageIndex: 0, pageSize: pagination.pageSize})
                        setSelectedParties(selected ? selected.map((s) => s.value) : [])
                      }}
                      menuPortalTarget={document.body}
                      styles={filterSelectStyles}
                    />
                  </Col>
                  <Col md={2}>
                    {/* Invisible label matches the Status/Parties label height above
                        their controls, so this checkbox sits top-aligned with the
                        actual control zone instead of anchored to the row's bottom
                        (which shifts once the Parties multi-select wraps chips). */}
                    <Form.Label className='d-block invisible'>Match</Form.Label>
                    <div
                      className='d-flex align-items-center'
                      style={{height: FILTER_CONTROL_HEIGHT}}
                    >
                      <Form.Check
                        type='checkbox'
                        id='strict-party-match'
                        label='Match all selected parties'
                        checked={strictPartyMatch}
                        onChange={(e) => {
                          setPagination({pageIndex: 0, pageSize: pagination.pageSize})
                          setStrictPartyMatch(e.target.checked)
                        }}
                      />
                    </div>
                  </Col>
                  <Col md={2}>
                    <Form.Label className='d-block invisible'>Reset</Form.Label>
                    <Button
                      variant='light'
                      className='d-flex align-items-center justify-content-center gap-2 w-100'
                      style={{height: FILTER_CONTROL_HEIGHT}}
                      onClick={handleResetFilters}
                    >
                      <span className='fa fa-rotate-left'></span>
                      Reset Filters
                    </Button>
                  </Col>
                  <Col md={3}>
                    <Form.Label className='d-block invisible'>WTO</Form.Label>
                    <div
                      className='d-flex align-items-center'
                      style={{height: FILTER_CONTROL_HEIGHT}}
                    >
                      <Form.Check
                        type='checkbox'
                        id='include-wto-agreements'
                        label='Include WTO Agreements'
                        checked={includeWtoAgreements}
                        onChange={(e) => {
                          setPagination({pageIndex: 0, pageSize: pagination.pageSize})
                          setIncludeWtoAgreements(e.target.checked)
                        }}
                      />
                    </div>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          <table
            className='gy-3 table table-striped table-bordered table-hover table-header-sticky'
            id='list_country_tradeflow'
          >
            <thead className='table-dark'>{displayHeader}</thead>
            <tbody className='gy-1 overflow-auto'>{displayBody}</tbody>
          </table>
          <Row className='my-3'>
            <Col md={6} className='d-flex justify-content-start align-items-center'>
              Display
              <Form.Select
                size='sm'
                className='w-80px mx-2'
                onChange={handleDisplayPageChange}
                value={pagination.pageSize}
              >
                <option value='10'>10</option>
                <option value='20'>20</option>
                <option value='50'>50</option>
                <option value='100'>100</option>
              </Form.Select>
              list country
            </Col>
            <Col md={6} className='d-flex justify-content-end'>
              <Pagination>
                <Pagination.Prev
                  onClick={() => handleNavigationPageChange('prev')}
                  disabled={pagination.pageIndex === 0}
                />
                {/* Start Dynamic Page */}
                {pages.map((page, index) => {
                  if (page === '...') {
                    return <Pagination.Ellipsis key={index} />
                  }
                  return (
                    <Pagination.Item
                      key={index}
                      active={page === pagination.pageIndex + 1}
                      onClick={() => {
                        setPagination({
                          ...pagination,
                          pageIndex: page - 1, // Convert 1-based page to 0-based index
                        })
                      }}
                    >
                      {page}
                    </Pagination.Item>
                  )
                })}
                {/* End Dynamic Page */}
                <Pagination.Next
                  onClick={() => handleNavigationPageChange('next')}
                  disabled={pagination.pageIndex >= dataTable.getPageCount() - 1}
                />
              </Pagination>
            </Col>
          </Row>
        </>
      ) : (
        <div className='d-flex align-items-center justify-content-center' style={{height: '50vh'}}>
          <Spinner animation='border' role='status' variant='primary' className='me-3' />
          <div className='loading-text'>
            Loading
            <span className='dot'>.</span>
            <span className='dot'>.</span>
            <span className='dot'>.</span>
          </div>
        </div>
      )}
    </div>
  )
}

export {ListAgreementTable}
